import {
  CATEGORY_CHANGE_REASON_OPTIONS,
  CATEGORY_OPTIONS,
  EXCLUSION_SUBCODE_OPTIONS,
} from "../../../constants/categoryConstants";

export const DEFAULT_CONTEXT_MAX_LENGTH = 500;

export const STATIC_CLASSIFICATION_METADATA = {
  categories: CATEGORY_OPTIONS,
  exclusionSubcodes: EXCLUSION_SUBCODE_OPTIONS,
  reasons: CATEGORY_CHANGE_REASON_OPTIONS,
  contextMaxLength: DEFAULT_CONTEXT_MAX_LENGTH,
};

export const normalizeFeedbackValue = (value) => {
  if (typeof value === "string") return value.trim();
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const firstValue = (values) => {
  const match = values.find(
    (value) => value !== undefined && value !== null && normalizeFeedbackValue(value)
  );
  return match === undefined ? "" : normalizeFeedbackValue(match);
};

const fallbackByCode = (fallback = []) =>
  new Map(
    fallback.map((option) => [normalizeFeedbackValue(option.code ?? option.value), option])
  );

const normalizeOptions = (values, fallback, valueKeys) => {
  if (!Array.isArray(values) || !values.length) return fallback;
  const knownByCode = fallbackByCode(fallback);
  const normalized = values
    .map((entry) => {
      if (typeof entry === "string") {
        const code = normalizeFeedbackValue(entry);
        const known = knownByCode.get(code);
        return known
          ? { ...known, code: known.code || code, value: known.value || code }
          : { code, value: code, label: code };
      }
      const value = firstValue(valueKeys.map((key) => entry?.[key]));
      if (!value) return null;
      const known = knownByCode.get(value);
      return {
        ...(known || {}),
        ...entry,
        code: entry.code || known?.code || value,
        value: entry.value || known?.value || value,
        label: entry.label || entry.name || known?.label || value,
        translationKey: entry.translationKey || known?.translationKey,
      };
    })
    .filter(Boolean);
  return normalized.length ? normalized : fallback;
};

export const normalizeClassificationMetadata = (response) => {
  const source = response?.result || response?.data || response || {};
  return {
    categories: normalizeOptions(
      source.categories || source.taxonomy?.categories,
      STATIC_CLASSIFICATION_METADATA.categories,
      ["code", "value", "id"]
    ),
    exclusionSubcodes: normalizeOptions(
      source.exclusion_subcodes ||
        source.exclusionSubcodes ||
        source.taxonomy?.exclusion_subcodes,
      STATIC_CLASSIFICATION_METADATA.exclusionSubcodes,
      ["code", "value", "id"]
    ),
    reasons: normalizeOptions(
      source.reason_codes || source.reasons || source.feedback_reasons,
      STATIC_CLASSIFICATION_METADATA.reasons,
      ["value", "code", "id"]
    ),
    contextMaxLength:
      Number(
        source.context_max_length ||
          source.feedback?.context_max_length ||
          source.limits?.context_max_length
      ) || DEFAULT_CONTEXT_MAX_LENGTH,
  };
};

const getCurrentCategory = (item) =>
  firstValue([item?.category, item?.Category]);

const getCurrentSubcode = (item) =>
  firstValue([
    item?.exclusion_subcode,
    item?.exclusionSubcode,
    item?.exclusionSubCode,
    item?.exclusion,
  ]);

const getServerOriginalCategory = (item) =>
  firstValue([
    item?.original_ai_category,
    item?.immutable_original_category,
    item?.original_category,
    item?.ai_category,
    item?.prediction_category,
    item?.classification_original?.category,
    item?.original_prediction?.category,
    item?.prediction?.category,
  ]);

const getServerOriginalSubcode = (item) =>
  firstValue([
    item?.original_ai_exclusion_subcode,
    item?.immutable_original_exclusion_subcode,
    item?.original_exclusion_subcode,
    item?.ai_exclusion_subcode,
    item?.prediction_exclusion_subcode,
    item?.classification_original?.exclusion_subcode,
    item?.original_prediction?.exclusion_subcode,
    item?.prediction?.exclusion_subcode,
  ]);

export const prepareClassificationItem = (item = {}, createKey) => {
  const currentCategory = getCurrentCategory(item);
  const currentSubcode = getCurrentSubcode(item);
  const serverOriginalCategory = getServerOriginalCategory(item);
  const serverOriginalSubcode = getServerOriginalSubcode(item);
  const originalCategory =
    serverOriginalCategory ||
    normalizeFeedbackValue(item.feedback_original_category) ||
    currentCategory;
  const originalSubcode =
    serverOriginalSubcode ||
    normalizeFeedbackValue(item.feedback_original_exclusion_subcode) ||
    currentSubcode;

  return {
    ...item,
    key: item.key || createKey(),
    feedback_original_category: originalCategory,
    feedback_original_exclusion_subcode: originalSubcode,
    feedback_baseline_category:
      normalizeFeedbackValue(item.feedback_baseline_category) || currentCategory,
    feedback_baseline_exclusion_subcode:
      normalizeFeedbackValue(item.feedback_baseline_exclusion_subcode) ||
      currentSubcode,
    feedback_change_reason: item.feedback_change_reason || "",
    feedback_change_context: item.feedback_change_context || "",
  };
};

export const hasPendingClassificationChange = (item = {}) => {
  const hasBaseline =
    Object.prototype.hasOwnProperty.call(item, "feedback_baseline_category") ||
    Object.prototype.hasOwnProperty.call(item, "feedback_original_category");
  if (!hasBaseline) return false;
  return (
    getCurrentCategory(item) !==
      normalizeFeedbackValue(
        item.feedback_baseline_category ?? item.feedback_original_category
      ) ||
    getCurrentSubcode(item) !==
      normalizeFeedbackValue(
        item.feedback_baseline_exclusion_subcode ??
          item.feedback_original_exclusion_subcode
      )
  );
};

export const hasClassificationOverride = (item = {}) => {
  if (
    !Object.prototype.hasOwnProperty.call(item, "feedback_original_category")
  ) {
    return false;
  }
  return (
    getCurrentCategory(item) !==
      normalizeFeedbackValue(item.feedback_original_category) ||
    getCurrentSubcode(item) !==
      normalizeFeedbackValue(item.feedback_original_exclusion_subcode)
  );
};

export const getClassificationFeedbackIdentity = (item = {}) => ({
  itemId: firstValue([item.item_id, item.itemId, item.identity?.item_id]),
  predictionId: firstValue([
    item.prediction_id,
    item.predictionId,
    item.prediction?.prediction_id,
  ]),
});

export const isClassificationFeedbackCapable = (item = {}) => {
  const { itemId, predictionId } = getClassificationFeedbackIdentity(item);
  return Boolean(itemId && predictionId);
};

export const getClassificationFeedbackUnavailableKey = (item = {}) => {
  if (isClassificationFeedbackCapable(item)) return "";
  const deliveryStatus = firstValue([
    item.classification_event_delivery_status,
    item.classificationEventDeliveryStatus,
    item.classification_event_outbox?.last_status,
    item.classificationEventOutbox?.last_status,
  ]);
  if (deliveryStatus === "retryable_failed") return "feedbackUnavailableRetrying";
  if (deliveryStatus === "disabled") return "feedbackUnavailableDisabled";
  if (deliveryStatus === "permanent_failed") return "feedbackUnavailableNotStored";
  return "feedbackUnavailableLegacy";
};

const DUPLICATED_CLASSIFICATION_FIELDS = new Set([
  "item_id",
  "itemId",
  "prediction_id",
  "predictionId",
  "prediction",
  "identity",
  "classification_run_id",
  "classificationRunId",
  "classification_override",
  "classificationOverride",
  "classification_original",
  "original_prediction",
  "original_category",
  "original_exclusion_subcode",
  "immutable_original_category",
  "immutable_original_exclusion_subcode",
  "prediction_category",
  "prediction_exclusion_subcode",
  "feedback_original_category",
  "feedback_original_exclusion_subcode",
  "feedback_baseline_category",
  "feedback_baseline_exclusion_subcode",
  "feedback_change_reason",
  "feedback_change_context",
  "event_delivery",
  "event_delivery_status",
  "feedback_event_delivery",
  "classification_event_delivery",
  "classification_event_outbox",
  "classificationEventOutbox",
  "taxonomy_version",
  "taxonomyVersion",
  "prompt_version",
  "promptVersion",
  "schema_version",
  "schemaVersion",
]);

export const sanitizeDuplicatedClassificationItem = (item = {}) =>
  Object.entries(item).reduce((copy, [key, value]) => {
    const normalizedKey = key
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .toLowerCase();
    if (
      DUPLICATED_CLASSIFICATION_FIELDS.has(key) ||
      normalizedKey.startsWith("original_ai_") ||
      normalizedKey.startsWith("feedback_original_") ||
      normalizedKey.startsWith("feedback_baseline_") ||
      normalizedKey.startsWith("feedback_change_") ||
      normalizedKey.includes("event_delivery") ||
      normalizedKey === "classification_event_outbox" ||
      /(taxonomy|prompt|schema)_version$/.test(normalizedKey)
    ) {
      return copy;
    }
    copy[key] = value;
    return copy;
  }, {});

export const getDuplicateClassificationItemErrors = (invoice = {}) => {
  const itemsById = new Map();
  (invoice?.extracted_invoice_data || []).forEach((page) => {
    (page?.treatments || []).forEach((treatment) => {
      (treatment?.Items || []).forEach((item) => {
        const { itemId } = getClassificationFeedbackIdentity(item);
        if (!itemId) return;
        const matchingItems = itemsById.get(itemId) || [];
        matchingItems.push(item);
        itemsById.set(itemId, matchingItems);
      });
    });
  });

  const errors = {};
  itemsById.forEach((items) => {
    if (items.length < 2) return;
    items.forEach((item) => {
      if (item.key) {
        errors[item.key] = { identity: "feedbackDuplicateItemId" };
      }
    });
  });
  return errors;
};

const optionValues = (options) =>
  new Set(
    (options || []).map((option) =>
      normalizeFeedbackValue(option.code ?? option.value)
    )
  );

export const validateClassificationItem = (
  item,
  metadata = STATIC_CLASSIFICATION_METADATA
) => {
  if (!hasPendingClassificationChange(item)) return {};

  const errors = {};
  const category = getCurrentCategory(item);
  const subcode = getCurrentSubcode(item);
  const reason = normalizeFeedbackValue(item.feedback_change_reason);
  const context = normalizeFeedbackValue(item.feedback_change_context);
  const categories = optionValues(metadata.categories);
  const subcodes = optionValues(metadata.exclusionSubcodes);
  const reasons = optionValues(metadata.reasons);
  const maxLength = metadata.contextMaxLength || DEFAULT_CONTEXT_MAX_LENGTH;

  if (!isClassificationFeedbackCapable(item)) {
    errors.identity = "feedbackIdentityRequired";
  }
  if (!category || !categories.has(category)) errors.category = "invalidCategory";
  if (!reason || !reasons.has(reason)) errors.reason = "feedbackReasonRequired";
  if (reason === "other" && !context) errors.context = "feedbackContextRequired";
  if (context.length > maxLength) errors.context = "feedbackContextTooLong";
  if (category === "E" && (!subcode || !subcodes.has(subcode))) {
    errors.subcode = "feedbackExclusionSubcodeRequired";
  } else if (category !== "E" && subcode) {
    errors.subcode = "feedbackNonExclusionSubcode";
  }

  return errors;
};

export const collectClassificationChanges = (
  invoice,
  metadata = STATIC_CLASSIFICATION_METADATA
) => {
  const changes = [];
  (invoice?.extracted_invoice_data || []).forEach((page, pageIndex) => {
    (page?.treatments || []).forEach((treatment, treatmentIndex) => {
      (treatment?.Items || []).forEach((item, itemIndex) => {
        if (!hasPendingClassificationChange(item)) return;
        changes.push({
          item,
          pageNo: page?.page_no ?? pageIndex + 1,
          treatmentOrder: treatment?.treatment_order ?? treatmentIndex + 1,
          itemOrder: item?.item_order ?? itemIndex + 1,
          errors: validateClassificationItem(item, metadata),
        });
      });
    });
  });
  return changes;
};

export const getInvoiceClassificationValidationErrors = (
  invoice,
  metadata = STATIC_CLASSIFICATION_METADATA
) => {
  const errors = getDuplicateClassificationItemErrors(invoice);
  collectClassificationChanges(invoice, metadata).forEach(
    ({ item, errors: itemErrors }) => {
      if (!Object.keys(itemErrors).length) return;
      errors[item.key] = {
        ...errors[item.key],
        ...itemErrors,
      };
    }
  );
  return errors;
};

export const buildClassificationFeedbackPayload = (change) => {
  if (!change?.item) return null;
  const { item, pageNo, treatmentOrder, itemOrder } = change;
  const { itemId, predictionId } = getClassificationFeedbackIdentity(item);
  if (!itemId || !predictionId) return null;

  return {
    schema_version: 1,
    prediction_id: predictionId,
    item: {
      item_id: itemId,
      page_no: pageNo,
      treatment_order: treatmentOrder,
      item_order: itemOrder,
    },
    decision: "corrected",
    correction: {
      category: getCurrentCategory(item),
      exclusion_subcode:
        getCurrentCategory(item) === "E" ? getCurrentSubcode(item) : "",
    },
    reason_code: normalizeFeedbackValue(item.feedback_change_reason),
    context: normalizeFeedbackValue(item.feedback_change_context),
  };
};

export const buildClassificationFeedbackSubmissions = (changes = []) => {
  const submissions = changes.map((change) => ({
    change,
    payload: buildClassificationFeedbackPayload(change),
  }));
  return {
    submissions,
    skipped: submissions.filter(({ payload }) => !payload),
  };
};

export const getFeedbackSignature = (payload) =>
  payload
    ? JSON.stringify({
        prediction_id: payload.prediction_id,
        item_id: payload.item.item_id,
        category: payload.correction.category,
        exclusion_subcode: payload.correction.exclusion_subcode,
        reason_code: payload.reason_code,
        context: payload.context,
      })
    : "";

export const markClassificationBaselineSaved = (item = {}) => ({
  ...item,
  feedback_baseline_category: getCurrentCategory(item),
  feedback_baseline_exclusion_subcode: getCurrentSubcode(item),
});
